import React from "react";
import {
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { MoneyField } from "../../fields/MoneyField";
import { SectionAccordion } from "./SectionAccordion";
import CabinIcon from "@mui/icons-material/Cabin";
import {
  MaterialSectionProps,
  ChangeCountMaterialSectionProps,
} from "../../../../types";

export interface MaterialSectionComponentProps {
  state: MaterialSectionProps;
  setState: (state: MaterialSectionProps) => void;
  formErrors: Record<string, string>;
  updateField: (field: string, value: string) => void;
  total: number;
  mode: "edit" | "review";
  pageVariant: "create" | "edit" | "view";
  initialState?: MaterialSectionProps;
  changeCount?: ChangeCountMaterialSectionProps;
  forceMobile?: boolean;
}

export function MaterialSection({
  state,
  setState,
  formErrors,
  updateField,
  total,
  mode,
  pageVariant,
  initialState,
  changeCount,
  forceMobile,
}: MaterialSectionComponentProps) {
  const isMobile = forceMobile === true;
  const isEditView = pageVariant !== "create";
  const isView = pageVariant === "view";
  const viewFieldSx = {
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
    "& .MuiOutlinedInput-input.Mui-disabled": { WebkitTextFillColor: "rgba(0,0,0,0.45)" },
    "& .MuiInputAdornment-root .MuiTypography-root": { color: "rgba(0,0,0,0.45)" },
  };
  const labelXs = isMobile ? 12 : (isEditView ? 6 : 9);
  const fieldXs = isMobile ? 12 : 3;

  function initialText(val: number | undefined): string {
    return val === undefined ? "" : val.toString().replace(/^0+/, "");
  }

  function changeCountText(count: number): string {
    return count > 1 ? ` (${count})` : "";
  }

  const dollarAdornment = (
    <InputAdornment position="start" sx={{ ml: 1 }}>$</InputAdornment>
  );

  function ViewField({ value }: { value: number | undefined }) {
    return (
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        disabled
        value={value ?? ""}
        InputProps={{ startAdornment: dollarAdornment }}
        sx={viewFieldSx}
      />
    );
  }

  return (
    <SectionAccordion
      id="material"
      title="Material"
      icon={
        <CabinIcon
          fontSize="large"
          sx={{ color: "#ACACAC" }}
        />
      }
      total={total}
      forceMobile={forceMobile}
    >
      {/* Material Budget/Category C */}
      <Grid item xs={labelXs} display="flex">
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{ alignSelf: "center" }}
        >
          Material Budget/Category C
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.MaterialBudget)}{" "}
            {changeCountText(changeCount?.MaterialBudget ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.MaterialBudget} />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            variant="outlined"
            size="small"
            fullWidth
            value={
              state.MaterialBudget === undefined
                ? ""
                : state.MaterialBudget.toString().replace(/^0+/, "")
            }
            onChange={(e) => updateField("MaterialBudget", e.target.value)}
            error={formErrors["MaterialBudget"] !== ""}
            helperText={formErrors["MaterialBudget"]}
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Material Budget/Category C"
            value={state.MaterialBudget ?? null}
            onChange={(v) =>
              updateField("MaterialBudget", v === null ? "" : String(v))
            }
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["MaterialBudget"] !== ""}
            helperText={formErrors["MaterialBudget"]}
          />
        )}
      </Grid>

      {/* Extra Material Budget/Category D */}
      <Grid item xs={labelXs} display="flex">
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{ alignSelf: "center" }}
        >
          Extra Material Budget/Category D
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body1" fontWeight="normal" sx={{ alignSelf: "center" }}>
            {initialText(initialState?.ExtraMaterialBudget)}{" "}
            {changeCountText(changeCount?.ExtraMaterialBudget ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.ExtraMaterialBudget} />
        ) : isEditView ? (
          <TextField
            type="text"
            disabled={mode === "review"}
            inputProps={{ inputMode: "numeric", min: 0 }}
            variant="outlined"
            size="small"
            fullWidth
            value={
              state.ExtraMaterialBudget === undefined
                ? ""
                : state.ExtraMaterialBudget.toString().replace(/^0+/, "")
            }
            onChange={(e) => updateField("ExtraMaterialBudget", e.target.value)}
            error={formErrors["ExtraMaterialBudget"] !== ""}
            helperText={formErrors["ExtraMaterialBudget"]}
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Extra Material Budget/Category D"
            value={state.ExtraMaterialBudget ?? null}
            onChange={(v) =>
              updateField("ExtraMaterialBudget", v === null ? "" : String(v))
            }
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["ExtraMaterialBudget"] !== ""}
            helperText={formErrors["ExtraMaterialBudget"]}
          />
        )}
      </Grid>

      {/* Special Material Budget */}
      <Grid item xs={labelXs} display="flex">
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{ alignSelf: "center" }}
        >
          Special Material Budget
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.SpecialMaterial)}{" "}
            {changeCountText(changeCount?.SpecialMaterial ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <ViewField value={state.SpecialMaterial} />
        ) : isEditView ? (
          <TextField
            type="text"
            disabled={mode === "review"}
            inputProps={{ inputMode: "numeric", min: 0 }}
            variant="outlined"
            size="small"
            fullWidth
            value={
              state.SpecialMaterial === undefined
                ? ""
                : state.SpecialMaterial.toString().replace(/^0+/, "")
            }
            onChange={(e) => updateField("SpecialMaterial", e.target.value)}
            error={formErrors["SpecialMaterial"] !== ""}
            helperText={formErrors["SpecialMaterial"]}
            InputProps={{ startAdornment: dollarAdornment }}
          />
        ) : (
          <MoneyField
            label="Special Material Budget"
            value={state.SpecialMaterial ?? null}
            onChange={(v) =>
              updateField("SpecialMaterial", v === null ? "" : String(v))
            }
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["SpecialMaterial"] !== ""}
            helperText={formErrors["SpecialMaterial"]}
          />
        )}
      </Grid>

      {/* Total Material */}
      <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"}
        mt={5}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ alignSelf: "center" }}
        >
          Total Material
        </Typography>
      </Grid>
      <Grid item xs={fieldXs} mt={isMobile ? 0 : 5}>
        <TextField
          type="number"
          inputProps={{ min: 0 }}
          disabled
          variant="filled"
          size="small"
          fullWidth
          value={total}
          InputProps={{ startAdornment: dollarAdornment }}
        />
      </Grid>
    </SectionAccordion>
  );
}
